// src/components/AGTFit/workout/LogExerciseDialog.js
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  IconButton,
  Slider,
  Grid,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { X, Plus, Minus, Dumbbell, Pause, Play } from 'lucide-react';
import agtfitService from '../../service/agtfitService';

// Color principal
const FIT_ORANGE = '#FF6B00';

// Colores para modo wallpaper
const WALLPAPER_COLORS = {
  bg: 'rgba(128, 128, 128, 0.3)',
  bgLight: 'rgba(128, 128, 128, 0.25)',
  border: 'rgba(128, 128, 128, 0.3)',
  text: '#FFFFFF',
  textSecondary: 'rgba(255, 255, 255, 0.7)'
};

// Estilo para diálogos estilo Apple como en ExerciseExplorer
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    [theme.breakpoints.up('sm')]: {
      borderRadius: '20px',
    },
    [theme.breakpoints.down('sm')]: {
      borderRadius: '0px',
      margin: 0, // Eliminar márgenes en móvil
      maxHeight: '100%', // Ocupar toda la altura
      height: '100%', // Forzar altura completa
    },
    boxShadow: theme.palette.actualMode === 'wallpaper'
      ? 'none'
      : (theme.palette.mode === 'dark'
        ? '0 20px 60px rgba(0, 0, 0, 0.5)'
        : '0 20px 60px rgba(0, 0, 0, 0.2)'),
    background: theme.palette.actualMode === 'wallpaper'
      ? WALLPAPER_COLORS.bg
      : (theme.palette.mode === 'dark'
        ? 'rgba(30, 30, 32, 0.95)'
        : 'rgba(255, 255, 255, 1)'),
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
    overflow: 'hidden',
    zIndex: theme.zIndex.modal + 1, // Aumentar zIndex para asegurar que esté por encima
  },
  // Asegurar que el diálogo está por encima de todo
  zIndex: theme.zIndex.modal + 1
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.5, 3),
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '15px',
  boxShadow: 'none',
  backgroundImage: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
  color: '#FFFFFF',
  '&:hover': {
    backgroundImage: 'linear-gradient(90deg, #FF8800 0%, #FF5500 100%)',
    boxShadow: 'none',
  },
}));

const CounterButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark' ? 'rgba(50, 50, 52, 0.8)' : 'rgba(245, 245, 250, 0.8)'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
  borderRadius: '50%',
  width: 36,
  height: 36,
  '&:hover': {
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? 'rgba(255,107,0,0.15)'
      : (theme.palette.mode === 'dark' ? 'rgba(60, 60, 62, 0.8)' : 'rgba(235, 235, 240, 0.8)'),
  }
}));

// Contenedor de media mejorado para ajustarse perfectamente
const MediaContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: 220,
  borderRadius: 16,
  overflow: 'hidden',
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.bgLight : '#FFFFFF', // Cambiado a blanco para que coincida con el fondo de los videos
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
}));

// Botón estilo Apple para video
const MediaControlButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 10,
  right: 10,
  width: 32,
  height: 32,
  backgroundColor: 'rgba(128, 128, 128, 0.6)', // Gris semi-transparente
  backdropFilter: 'blur(5px)',
  WebkitBackdropFilter: 'blur(5px)',
  borderRadius: '50%',
  padding: 6,
  '&:hover': {
    backgroundColor: 'rgba(128, 128, 128, 0.8)',
  },
  zIndex: 2,
  boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
  transition: 'all 0.2s ease-in-out',
}));

// Campo de texto estilizado como en ExerciseExplorer
const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? WALLPAPER_COLORS.bgLight
      : (theme.palette.mode === 'dark' ? 'rgba(50, 50, 52, 0.8)' : 'rgba(245, 245, 250, 0.8)'),
    backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
    '& fieldset': {
      borderColor: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.border : 'transparent',
    },
    '&:hover fieldset': {
      borderColor: theme.palette.actualMode === 'wallpaper'
        ? WALLPAPER_COLORS.border
        : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'),
    },
    '&.Mui-focused fieldset': {
      borderColor: FIT_ORANGE,
    },
  }
}));

// Contenedor para los datos del ejercicio
const ExerciseInfoBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 16,
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark' ? 'rgba(30, 30, 32, 0.8)' : '#FFFFFF'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  border: `1px solid ${theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.border
    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)')}`,
  marginBottom: theme.spacing(2)
}));

// Caja de estadísticas
const StatsBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 16,
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? 'rgba(255, 107, 0, 0.2)'
    : (theme.palette.mode === 'dark' ? 'rgba(255, 107, 0, 0.15)' : 'rgba(255, 107, 0, 0.1)'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
}));

const LogExerciseDialog = ({ open, onClose, exercise }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [sets, setSets] = useState(3);
  const [reps, setReps] = useState(10);
  const [weight, setWeight] = useState(0);
  const [duration, setDuration] = useState(0); // en minutos
  const [submitting, setSubmitting] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);
  const videoRef = React.useRef(null);
  
  // Calcular calorías basadas en la duración y el ejercicio base
  const calculateCalories = () => {
    const baseCalories = exercise?.baseCalories || 5;
    const setCalories = baseCalories * sets;
    return Math.round(setCalories + (duration * 3)); // 3 calorías adicionales por minuto
  };
  
  // Manejar cambio en sets
  const handleSetsChange = (newValue) => {
    if (newValue >= 1 && newValue <= 10) setSets(newValue);
  };
  
  // Manejar cambio en reps
  const handleRepsChange = (newValue) => {
    if (newValue >= 1 && newValue <= 100) setReps(newValue);
  };
  
  // Manejar cambio en peso
  const handleWeightChange = (event) => {
    const value = parseFloat(event.target.value);
    if (!isNaN(value) && value >= 0) setWeight(value);
    else setWeight(0);
  };
  
  // Manejar cambio en duración
  const handleDurationChange = (event, newValue) => {
    setDuration(newValue);
  };
  
  // Función para alternar reproducción/pausa
  const toggleVideoPlayback = (e) => {
    e.stopPropagation(); // Evitar que el clic se propague al contenedor
    
    if (videoRef.current) {
      if (isVideoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsVideoPlaying(!isVideoPlaying);
    }
  };
  
  // Registrar el ejercicio
  const handleLogExercise = async () => {
    if (!exercise) return;
    
    setSubmitting(true);
    
    try {
      const exerciseData = {
        name: exercise.name,
        type: exercise.primaryMuscle,
        sets,
        reps,
        weight,
        duration,
        caloriesBurned: calculateCalories(),
        catalogExerciseId: exercise.exerciseId,
        date: new Date().toISOString().split('T')[0]
      };
      
      const response = await agtfitService.logExercise(exerciseData);
      
      if (response.success) {
        onClose(true, exerciseData); // Pasar los datos del ejercicio al cerrar
      } else {
        console.error('Error logging exercise:', response.error);
      }
    } catch (error) {
      console.error('Error logging exercise:', error);
    } finally {
      setSubmitting(false);
    }
  };
  
  // Renderizar media (imagen o video) con ajuste perfecto y loop para videos
  const renderMedia = () => {
    if (!exercise) return null;
    
    if (exercise.videoUrl) {
      return (
        <MediaContainer>
          <video 
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            style={{ 
              maxWidth: '100%', 
              maxHeight: '100%', 
              objectFit: 'contain'
            }}
          >
            <source src={exercise.videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          
          {/* Botón de control de video estilo Apple */}
          <MediaControlButton onClick={toggleVideoPlayback}>
            {isVideoPlaying ? 
              <Pause size={16} color="#FFFFFF" /> : 
              <Play size={16} color="#FFFFFF" />
            }
          </MediaControlButton>
        </MediaContainer>
      );
    } else if (exercise.imageUrl) {
      return (
        <MediaContainer>
          <img 
            src={exercise.imageUrl} 
            alt={exercise.name}
            style={{ 
              maxWidth: '100%', 
              maxHeight: '100%', 
              objectFit: 'contain'
            }}
          />
        </MediaContainer>
      );
    } else {
      return (
        <MediaContainer>
          <Dumbbell size={60} color={FIT_ORANGE} opacity={0.3} />
        </MediaContainer>
      );
    }
  };
  
  if (!exercise) return null;
  
  return (
    <StyledDialog
      open={open}
      onClose={() => onClose(false)}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
    >
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        px: 3,
        py: 2,
        borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'}`
      }}>
        <Typography variant="h5" fontWeight={600}>
          Log Exercise
        </Typography>
        
        <IconButton 
          onClick={() => onClose(false)} 
          sx={{ 
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(60, 60, 62, 0.8)' : 'rgba(240, 240, 245, 0.8)',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(70, 70, 72, 0.8)' : 'rgba(230, 230, 235, 0.8)',
            }
          }}
        >
          <X size={20} />
        </IconButton>
      </Box>
      
      <DialogContent sx={{ px: 3, py: 3 }}>
        {/* Mostrar imagen o video */}
        {renderMedia()}
        
        <ExerciseInfoBox>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            {exercise.name}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {exercise.primaryMuscle && exercise.primaryMuscle.charAt(0).toUpperCase() + exercise.primaryMuscle.slice(1).toLowerCase()} 
            {exercise.equipment && ` • ${exercise.equipment.charAt(0).toUpperCase() + exercise.equipment.slice(1).toLowerCase()}`}
            {!exercise.equipment && ' • No Equipment'}
          </Typography>
          
          {exercise.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {exercise.description}
            </Typography>
          )}
        </ExerciseInfoBox>
        
        <Divider sx={{ my: 2 }} />
        
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Sets
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CounterButton onClick={() => handleSetsChange(sets - 1)} disabled={sets <= 1}>
                <Minus size={16} />
              </CounterButton>
              <Typography variant="h6" sx={{ mx: 2, width: 20, textAlign: 'center' }}>
                {sets}
              </Typography>
              <CounterButton onClick={() => handleSetsChange(sets + 1)} disabled={sets >= 10}>
                <Plus size={16} />
              </CounterButton>
            </Box>
          </Grid>
          
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Reps
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CounterButton onClick={() => handleRepsChange(reps - 1)} disabled={reps <= 1}>
                <Minus size={16} />
              </CounterButton>
              <Typography variant="h6" sx={{ mx: 2, width: 20, textAlign: 'center' }}>
                {reps}
              </Typography>
              <CounterButton onClick={() => handleRepsChange(reps + 1)} disabled={reps >= 100}>
                <Plus size={16} />
              </CounterButton>
            </Box>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Weight (kg)
            </Typography>
            <StyledTextField
              type="number"
              value={weight}
              onChange={handleWeightChange}
              variant="outlined"
              size="small"
              fullWidth
              inputProps={{ min: 0, step: 1 }}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Duration (minutes)
            </Typography>
            <Slider
              value={duration}
              onChange={handleDurationChange}
              min={0}
              max={60}
              step={1}
              valueLabelDisplay="auto"
              sx={{
                color: FIT_ORANGE,
                '& .MuiSlider-thumb': {
                  height: 24,
                  width: 24,
                  backgroundColor: '#fff',
                  border: `2px solid ${FIT_ORANGE}`,
                  '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
                    boxShadow: '0 0 0 8px rgba(255, 107, 0, 0.16)',
                  },
                }
              }}
            />
          </Grid>
          
          <Grid item xs={12}>
            <StatsBox>
              <Typography variant="body1" fontWeight={500}>
                Estimated calories:
              </Typography>
              <Typography variant="h6" fontWeight={600} sx={{ 
                backgroundImage: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                {calculateCalories()} kcal
              </Typography>
            </StatsBox>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={() => onClose(false)} 
          sx={{ 
            color: theme.palette.text.secondary,
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '15px',
          }}
        >
          Cancel
        </Button>
        <ActionButton 
          onClick={handleLogExercise}
          disabled={submitting}
        >
          {submitting ? 'Logging...' : 'Log Exercise'}
        </ActionButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default LogExerciseDialog;